import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import AddBoxIcon from "@mui/icons-material/AddBox";
import SearchIcon from "@mui/icons-material/Search";
import {
	Alert,
	Button,
	CircularProgress,
	FormControlLabel,
	Grid2,
	IconButton,
	GlobalStyles,
	Snackbar,
	Switch,
	TextField,
	Typography,
} from "@mui/material";
import type {
	ColDef,
	DefaultMenuItem,
	GetContextMenuItemsParams,
	GridApi,
	MenuItemDef,
} from "ag-grid-community";
import "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useSearchParams } from "react-router-dom";
import { globalDefaultColDef } from "@setup/ag-grid/mesf-theme";
import { useAssetDetailsContext } from "../../../context/assetDetailsContext";
import type { Assembly } from "@/pages/asset-details/models/Assembly";
import {
	createAssembly,
	deleteAssembly,
	getAssemblies,
	updateAssembly,
} from "../../../repositories/AssetDetailsRepositorie";
import { AssemblyDetailDialog } from "./components/AssemblyDetailDialog";
import {
	AssemblyBothResourcesDetail,
	AssemblyRowCell,
} from "../AssembliesPanel/AssembliesMasterDetailPanel";

type Props = {
	searchStatistics: Function;
};

const emptyForm = { Name: "" };

const getCompactPath = (fullPath: string) => {
	if (!fullPath) return "";
	const parts = fullPath.split("/");
	if (parts.length <= 3) return fullPath;
	return `${parts[0]}/.../${parts[parts.length - 2]}/${parts[parts.length - 1]}`;
};

// Asset column for the Assemblies tab: the owning asset (ParentAssetId) path, as a clickable
// link that navigates there — same behaviour as the Asset column in Documents / Parts. Plain
// text when it is the asset we already stand on.
const AssetLinkCell = ({ data }: { data: Assembly }) => {
	const navigate = useNavigate();
	const theme = useTheme();
	const {
		state: { assetSearchId },
	} = useAssetDetailsContext();
	if (!data) return null;
	const path = getCompactPath(data.FullPath || "");
	if (!path) return null;
	const targetId = data.ParentAssetId;
	const isCurrent =
		targetId != null && String(targetId) === String(assetSearchId);
	if (targetId == null || isCurrent) {
		return <span title={data.FullPath || ""}>{path}</span>;
	}
	return (
		<span
			title={data.FullPath || ""}
			className="assembly-asset-link"
			style={{ color: theme.palette.primary.main, cursor: "pointer" }}
			onClick={() =>
				navigate(
					`/asset-details/${targetId}?resource=assemblies&assets=true&statistics=true&workOrders=true`,
				)
			}
		>
			{path}
		</span>
	);
};

export const AssemblyGrid = ({ searchStatistics }: Props) => {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const resource = searchParams.get("resource") ?? "";

	const {
		state: { assetSearchId },
	} = useAssetDetailsContext();

	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [assemblies, setAssemblies] = useState<Assembly[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState("");
	const [successMessage, setSuccessMessage] = useState("");
	const [quickFilterText, setQuickFilterText] = useState("");
	const [includeDescendants, setIncludeDescendants] = useState(true);
	const [rowSelected, setRowSelected] = useState<Assembly | null>(null);

	// form dialog (create / edit)
	const [formOpen, setFormOpen] = useState(false);
	const [formMode, setFormMode] = useState<"create" | "edit">("create");
	const [form, setForm] = useState(emptyForm);
	const [saving, setSaving] = useState(false);

	// delete dialog
	const [deleteOpen, setDeleteOpen] = useState(false);

	// detail dialog
	const [detailOpen, setDetailOpen] = useState(false);
	const [detailAssembly, setDetailAssembly] = useState<Assembly | null>(null);

	const search = useCallback(async () => {
		if (!assetSearchId || assetSearchId === 0) return;
		setIsLoading(true);
		const resp = await getAssemblies(assetSearchId, includeDescendants);
		if (resp.ok) {
			setAssemblies(resp.data?.tables?.[0]?.rows ?? []);
		} else {
			setError(resp.message);
		}
		setIsLoading(false);
	}, [assetSearchId, includeDescendants]);

	useEffect(() => {
		if (resource === "assemblies" && assetSearchId && assetSearchId !== 0) {
			search();
		}
	}, [resource, assetSearchId, includeDescendants, search]);

	// Assemblies tab is a full resource table, so it keeps proper columns (Assembly / Asset /
	// Parts / Drawings). The Assembly column carries our own expand chevron; expanding shows the
	// assembly's parts and drawings inline.
	const columnDefs = useMemo<ColDef[]>(
		() => [
			{
				field: "AssetName",
				headerName: "Assembly",
				// Real names run past 40 chars ("430 Horizontal Spindle Carrier Chock Assembly"), so
				// the old 200px ceiling wrapped every one of them. Wide enough for those on one line,
				// keeping wrapText for the longer ones: the name ends in the part code, which an
				// ellipsis would be the first thing to eat.
				flex: 2,
				minWidth: 180,
				maxWidth: 360,
				// Left-align the header to sit over the assembly names (see GlobalStyles below).
				headerClass: "assembly-name-header",
				cellRenderer: AssemblyRowCell,
				cellStyle: {
					display: "flex",
					alignItems: "center",
					justifyContent: "flex-start",
					paddingLeft: "8px",
				},
				getQuickFilterText: (p) => p.data?.AssetName || "",
			},
			{
				field: "FullPath",
				headerName: "Asset",
				flex: 3,
				minWidth: 220,
				cellRenderer: AssetLinkCell,
				getQuickFilterText: (p) => p.data?.FullPath || "",
			},
			{
				field: "PartsCount",
				headerName: "Parts",
				width: 90,
				maxWidth: 90,
				filter: false,
			},
			{
				field: "DrawingsCount",
				headerName: "Drawings",
				width: 100,
				maxWidth: 100,
				filter: false,
			},
		],
		[],
	);

	const openCreate = useCallback(() => {
		setFormMode("create");
		setForm(emptyForm);
		setFormOpen(true);
	}, []);

	const openEdit = useCallback((row: Assembly) => {
		setFormMode("edit");
		setRowSelected(row);
		setForm({ Name: row.AssetName ?? "" });
		setFormOpen(true);
	}, []);

	const openDetail = useCallback((row: Assembly) => {
		setDetailAssembly(row);
		setDetailOpen(true);
	}, []);

	const handleSave = useCallback(async () => {
		if (!form.Name.trim()) {
			setError("Assembly name is required");
			return;
		}
		setSaving(true);
		const resp =
			formMode === "edit" && rowSelected
				? await updateAssembly(rowSelected.AssetId, form.Name.trim())
				: await createAssembly(assetSearchId as number, form.Name.trim());
		setSaving(false);
		if (!resp.ok) {
			setError(resp.message);
			return;
		}
		setFormOpen(false);
		search();
		searchStatistics?.();
		setSuccessMessage(
			formMode === "create"
				? "Assembly created successfully"
				: "Assembly updated successfully",
		);
		// On a brand-new assembly, open the detail modal right away so the user can
		// select its parts and drawings without an extra click.
		if (formMode === "create") {
			const newAssetId = resp.data?.tables?.[0]?.rows?.[0]?.AssetId;
			if (newAssetId) {
				setDetailAssembly({
					AssetId: newAssetId,
					ParentAssetId: assetSearchId as number,
					AssetName: form.Name.trim(),
					FullPath: null,
					PartsCount: 0,
					DrawingsCount: 0,
				});
				setDetailOpen(true);
			}
		}
	}, [form, formMode, rowSelected, assetSearchId, search, searchStatistics]);

	const handleDelete = useCallback(async () => {
		if (!rowSelected) return;
		const resp = await deleteAssembly(rowSelected.AssetId);
		if (!resp.ok) {
			setError(resp.message);
			return;
		}
		setDeleteOpen(false);
		search();
		searchStatistics?.();
		setSuccessMessage("Assembly deleted successfully");
	}, [rowSelected, search, searchStatistics]);

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const row = params.node?.data as Assembly | undefined;
			if (!row) return [];
			// NOTE: do not setState here — calling setRowSelected while AG Grid builds the menu
			// re-renders the grid and closes the just-opened menu. Each action sets the row itself.
			// An assembly is owned by the asset it hangs from (ParentAssetId). "Go to Asset"
			// only makes sense for an assembly of a descendant asset; when we already stand on
			// the owning asset it is hidden. Edit is gated to the owning asset; Delete is always
			// listed but disabled (with an explaining tooltip) off the owning asset.
			const owned = Number(row.ParentAssetId) === Number(assetSearchId);
			const items: (DefaultMenuItem | MenuItemDef)[] = [
				{
					name: "Manage Parts & Drawings",
					action: () => openDetail(row),
					icon: '<i class="fas fa-list"></i>',
				},
			];
			if (!owned) {
				items.push({
					name: "Go to Asset",
					action: () => {
						const sp = new URLSearchParams(searchParams);
						sp.set("resource", "assemblies");
						navigate(`/asset-details/${row.ParentAssetId}?${sp.toString()}`, {
							replace: true,
						});
					},
					icon: '<i class="fas fa-external-link-alt"></i>',
				});
			}
			if (owned) {
				items.push({
					name: "Edit Assembly",
					action: () => openEdit(row),
					icon: '<i class="fas fa-pen"></i>',
				});
			}
			// Delete is always shown so the action is discoverable, but disabled off the owning
			// asset (it can only be deleted from there). The tooltip explains why; the not-allowed
			// cursor is softened and the tooltip sped up via grid config / CSS below.
			items.push({
				name: "Delete Assembly",
				disabled: !owned,
				tooltip: owned
					? undefined
					: 'Go to this assembly\'s asset ("Go to Asset") to delete it',
				action: () => {
					if (!owned) return;
					setRowSelected(row);
					setDeleteOpen(true);
				},
				icon: '<i class="fas fa-trash"></i>',
			});
			return items;
		},
		[openDetail, openEdit, assetSearchId, searchParams, navigate],
	);

	const handleQuickFilterChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const value = event.target.value;
			setQuickFilterText(value);
			gridApi?.setGridOption("quickFilterText", value);
		},
		[gridApi],
	);

	return (
		<>
			<GlobalStyles
				styles={{
					// The theme centers headers with `justify-content: center !important`, so the
					// override needs !important (its two-class selector then wins on specificity).
					// 24px ≈ the assembly name's start (chevron 16 + gap 8).
					".assembly-name-header .ag-header-cell-label": {
						justifyContent: "flex-start !important",
						textAlign: "left !important",
						paddingLeft: "24px !important",
					},
					".assembly-asset-link:hover": { textDecoration: "underline" },
				}}
			/>
			<div
				style={{
					height: "100%",
					width: "100%",
					display: "flex",
					flexDirection: "column",
				}}
			>
				<div
					style={{
						display: "flex",
						alignItems: "center",
						gap: 8,
						padding: "8px 0px",
						flexShrink: 0,
					}}
				>
					<TextField
						style={{ flex: 1 }}
						variant="outlined"
						placeholder="Search assemblies..."
						value={quickFilterText}
						onChange={handleQuickFilterChange}
						slotProps={{
							input: {
								startAdornment: (
									<SearchIcon color="action" style={{ marginRight: 8 }} />
								),
							},
						}}
						size="small"
					/>
					<FormControlLabel
						control={
							<Switch
								checked={includeDescendants}
								onChange={(e) => setIncludeDescendants(e.target.checked)}
								color="primary"
							/>
						}
						label="Include descendant assets"
						style={{ marginLeft: 16, marginRight: 16 }}
					/>
					<IconButton
						onClick={openCreate}
						aria-label="addAssembly"
						color="primary"
					>
						<AddBoxIcon style={{ fontSize: 40 }} />
					</IconButton>
				</div>

				<div style={{ flex: 1, minHeight: 0, width: "100%" }}>
					<AgGridReact
						onGridReady={(params) => setGridApi(params.api)}
						rowData={assemblies}
						getRowId={(p) => String(p.data.AssetId)}
						columnDefs={columnDefs}
						defaultColDef={globalDefaultColDef}
						// Show the disabled-item explanation promptly (AG Grid's default menu tooltip
						// delay is 2s, which is what made it feel like it appeared "after a while").
						tooltipShowDelay={300}
						headerHeight={32}
						rowHeight={40}
						animateRows={true}
						masterDetail
						detailRowAutoHeight
						isRowMaster={() => true}
						detailCellRenderer={AssemblyBothResourcesDetail}
						// Rebuild the expanded detail when the master row's data changes (e.g. after
						// saving parts/drawings), so the inline tables refetch instead of showing stale
						// rows while the count columns already updated.
						detailCellRendererParams={{ refreshStrategy: "everything" }}
						getContextMenuItems={getContextMenuItems}
						onRowDoubleClicked={(e) => openDetail(e.data as Assembly)}
						loading={isLoading}
						quickFilterText={quickFilterText}
					/>
				</div>

				{/* Create / Edit dialog */}
				<MesfModal
					title={formMode === "create" ? "NEW ASSEMBLY" : "EDIT ASSEMBLY"}
					open={formOpen}
					maxWidth="sm"
					handleClose={() => setFormOpen(false)}
				>
					<MesfModal.Content dividers>
						<Grid2 container spacing={1}>
							<Grid2 size={{ xs: 12 }}>
								<TextField
									label="Assembly name"
									required
									value={form.Name}
									onChange={(e) =>
										setForm((f) => ({ ...f, Name: e.target.value }))
									}
									inputProps={{ maxLength: 100 }}
									margin="dense"
									fullWidth
								/>
							</Grid2>
						</Grid2>
					</MesfModal.Content>
					<MesfModal.Actions sx={{ px: 2, pt: 1, pb: 2 }}>
						<Grid2 container spacing={2} justifyContent="flex-end" width="100%">
							<Grid2 size={{ xs: 6, md: 4 }}>
								<Button
									fullWidth
									variant="contained"
									color="inherit"
									onClick={() => setFormOpen(false)}
								>
									Cancel
								</Button>
							</Grid2>
							<Grid2 size={{ xs: 6, md: 4 }}>
								<Button
									fullWidth
									variant="contained"
									color="primary"
									disabled={saving}
									onClick={handleSave}
									startIcon={
										saving ? (
											<CircularProgress size="1rem" color="inherit" />
										) : undefined
									}
								>
									Save
								</Button>
							</Grid2>
						</Grid2>
					</MesfModal.Actions>
				</MesfModal>

				{/* Delete confirm */}
				<MesfModal
					title="DELETE ASSEMBLY"
					open={deleteOpen}
					maxWidth="sm"
					handleClose={() => setDeleteOpen(false)}
				>
					<MesfModal.Content dividers>
						<Typography>
							Delete assembly <b>{rowSelected?.AssetName}</b> and all its part /
							drawing links?
						</Typography>
					</MesfModal.Content>
					<MesfModal.Actions sx={{ px: 2, pt: 1, pb: 2 }}>
						<Grid2 container spacing={2} justifyContent="flex-end" width="100%">
							<Grid2 size={{ xs: 6, md: 4 }}>
								<Button
									fullWidth
									variant="contained"
									color="inherit"
									onClick={() => setDeleteOpen(false)}
								>
									Cancel
								</Button>
							</Grid2>
							<Grid2 size={{ xs: 6, md: 4 }}>
								<Button
									fullWidth
									variant="contained"
									color="error"
									onClick={handleDelete}
								>
									Delete
								</Button>
							</Grid2>
						</Grid2>
					</MesfModal.Actions>
				</MesfModal>

				{/* Detail (parts + drawings) */}
				<AssemblyDetailDialog
					open={detailOpen}
					assembly={detailAssembly}
					onClose={(changed) => {
						setDetailOpen(false);
						if (changed) {
							search();
							searchStatistics?.();
						}
					}}
				/>

				<ErrorModal error={error} onHide={() => setError("")} />
				<Snackbar
					open={!!successMessage}
					autoHideDuration={4000}
					onClose={() => setSuccessMessage("")}
					anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
				>
					<Alert severity="success" onClose={() => setSuccessMessage("")}>
						{successMessage}
					</Alert>
				</Snackbar>
			</div>
		</>
	);
};
